import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { convertHelpers } from '../../common/convert';
import { gEnums } from '../../enums/globalEnums';
import { eventInfoHandlers, eventInfoInitialState, eventInfoReducer } from '../reducers/EventInfoReducer';
import { AppSettingsContext } from './AppSettingsContext';
import { AppUsersContext } from './AppUsersContext';
import { PapsContext } from './PapsContext';
import { ServiceWorkerContext } from './ServiceWorkerContext';
import { CurrentSettingsContext } from './SettingsDataContext';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';
import { useAppUsersInfo } from './useHelpers/useAppUsersInfo';
import { useEventInfo } from './useHelpers/useEventInfo';
import { useNotificationsInfo } from './useHelpers/useNotificationsInfo';

/** gets the data for the uiItem.
 * @returns viewListData, confirmation */
export const EventInfoContext = createContext();

/** gets the data for the uiItem.
 * @returns viewListData,  
 * @returns confirmation,  */
const EventInfoProvider = (props) => {

  // ParentContext not available

  const appUserAccess = useAppUserAccess()
  const { accessLevel } = appUserAccess ?? {}

  const aat = gEnums.appAreaTypes
  const svc = [aat.clients, aat.clubs, aat.districts, aat.event, aat.leagues, aat.schools, aat.organizations, aat.client]

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});

  // appUsersContext
  const appUsersContext = useContext(AppUsersContext);
  const { appUsers_handlers } = appUsersContext

  // papsContext
  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { appArea, pathViews, cacheKey, pathModes } = paps_state ?? {}

  // serviceWorkerContext
  const serviceWorkerContext = useContext(ServiceWorkerContext)
  const { serviceWorker_state } = serviceWorkerContext ?? {}
  const { cache_info } = serviceWorker_state ?? {}
  const { cache_groups } = cache_info ?? {}
  const { cache_event } = cache_groups ?? {}
  const cache_currentEvent = cache_event && pathViews && pathViews.events ? cache_event[pathViews.events] : {}
  const { cache_collections: _cache_collections, _timeStamps: _timeStamps_cache } = cache_currentEvent ?? {}

  // appSettingsContext
  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_handlers } = appSettingsContext ?? {}

  // pageContext 
  const { appDataSource, appUserDataSettings } = aps_global ?? {}
  const { appUserCollection, appUserCollections, allowPresenceTracking } = aps_appUserSettings ?? {}
  const { useStartDataCollections, getAllDataCollections, startDataCollections } = appDataSource ?? {}

  const _appUserCollections = appUserCollections ? appUserCollections : []
  if (_appUserCollections.length === 0 && appUserCollection) { _appUserCollections.push(appUserCollection) }

  // eventInfoInitialState
  // mainDb, mainStorage, database_fns
  const initState = eventInfoInitialState({ pathViews, appDataSource, aps_viewItems })
  const [eventInfo_state, dispatch] = useReducer(eventInfoReducer, initState);
  const eventInfo_handlers = useMemo(
    () => eventInfoHandlers(dispatch),
    [dispatch]
  );

  const { eventInfo } = useEventInfo()
  const notificationsInfo = useNotificationsInfo()
  const { appUsersInfo } = useAppUsersInfo()

  // console.log('eventInfo', eventInfo)
  // console.log('currentSettings_state', currentSettings_state)

  const infoList = [eventInfo, notificationsInfo, appUsersInfo];

  const { noEventAccess, eventData, staticViews, staticEventKey, appDataDocuments, appGlobalData, appGlobalDataFromCache, clientProfiles } = eventInfo_state ?? {}
  const { _itemKey: eventItemKey, name: eventName } = eventData ?? {}

  useEffect(() => {
    if (!pathViews.events) return;
    const { events: events_appUser } = appUserAccess ?? {}
    let allowAccess = false;
    const appUserHasEvent = events_appUser?.[pathViews.events] ? true : false
    if (accessLevel >= gEnums.accessLevels.appAdmin.value) { allowAccess = true }
    if (appUserHasEvent) { allowAccess = true }
    if (allowAccess) {
      infoList.forEach((info) => {
        if (info) eventInfo_handlers.handleInit_eventInfo(info);
        // eventInfo_handlers.handleInit_noEventAccess()
      });
    } else {
      eventInfo_handlers.handleInit_noEventAccess()
    }
    // eslint-disable-next-line
  }, [eventInfo, notificationsInfo, appUsersInfo, accessLevel]);

  const getStaticName = (staticView, staticValue, returnFound) => {
    const _staticView = staticView === 'guests' ? 'attendees' : staticView
    if (staticViews && staticViews[_staticView] && staticViews[_staticView][staticValue]) {
      const svv = staticViews[_staticView][staticValue]
      if (returnFound) {
        return { sv: convertHelpers.getItemName(svv) }
      } else {
        return convertHelpers.getItemName(svv)
      }
    } else {
      if (returnFound) {
        return { sv: staticValue, notFound: true }
      } else {
        return staticValue
      }
    }
  }

  useEffect(
    () => {
      if (clientProfiles) {
        eventInfo_handlers.handleSet_appUserUrls(clientProfiles, appUsersInfo)
      }
    },
    // eslint-disable-next-line
    [clientProfiles]
  )

  useEffect(
    () => {
      if (eventName) {
        appSettings_handlers && appSettings_handlers.handleAmmend_appHistory(eventName, paps_state?.pathName)
      }
    },
    // eslint-disable-next-line
    [eventItemKey, eventName]
  )
  useEffect(
    () => {
      if (appDataDocuments) {
        if (appUserCollection && appDataDocuments[appUserCollection]) {
          appUsers_handlers.handleSet_eventAppUsers(appDataDocuments[appUserCollection])
        }
      }
    },
    // eslint-disable-next-line
    [appDataDocuments]
  )

  const eventInfo_fns = {
    getStaticName: (staticView, staticValue, returnFound) => getStaticName(staticView, staticValue, returnFound)
  }


  if (noEventAccess) {
    // return <FullPageWrapper
    //   topperCaption={'No Event Access'}
    //   content={<UserEvents />}
    // />
  }

  if (svc.includes(appArea)) {
    if (staticEventKey) {
      return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
        {props.children}
      </EventInfoContext.Provider>
    } else {
      return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
        {props.children}
      </EventInfoContext.Provider>
    }
  } else {
    return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
      {props.children}
    </EventInfoContext.Provider>
  }
}

export default EventInfoProvider 